import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { formatDateDMY } from '../../lib/format'
import { CalendarRangePicker } from './CalendarRangePicker'
import { Icon } from './Icon'

// ── ISO date helpers ──────────────────────────────────────────────────────────

function isoToday(): string {
  return new Date().toISOString().slice(0, 10)
}

function isoDaysAgo(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

function isoStartOfYear(year?: number): string {
  return `${year ?? new Date().getFullYear()}-01-01`
}

function isoEndOfYear(year?: number): string {
  return `${year ?? new Date().getFullYear()}-12-31`
}

// ── Presets ───────────────────────────────────────────────────────────────────

export interface DateRangePreset {
  label: string
  getDates: () => { from: string; to: string }
}

function isoDaysAhead(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

// Default presets are backward-looking (historical reporting surfaces like
// HR Insights / HR Payroll). Forward-looking surfaces (Manager Dashboard,
// HR coverage planning) should pass their own `presets` array.
const DEFAULT_PRESETS: readonly DateRangePreset[] = [
  { label: 'Last 30 days',  getDates: () => ({ from: isoDaysAgo(30),  to: isoToday() }) },
  { label: 'Last 90 days',  getDates: () => ({ from: isoDaysAgo(90),  to: isoToday() }) },
  { label: 'Last 6 months', getDates: () => ({ from: isoDaysAgo(180), to: isoToday() }) },
  {
    label: 'This year',
    getDates: () => ({ from: isoStartOfYear(), to: isoToday() }),
  },
  {
    label: 'Last year',
    getDates: () => {
      const y = new Date().getFullYear() - 1
      return { from: isoStartOfYear(y), to: isoEndOfYear(y) }
    },
  },
]

// Forward-looking presets for HR / manager planning surfaces. Exported so
// callers can use a shared canonical set instead of re-deriving them.
export const FORWARD_PRESETS: readonly DateRangePreset[] = [
  { label: 'Next two weeks', getDates: () => ({ from: isoToday(), to: isoDaysAhead(14) }) },
  { label: 'Next 30 days', getDates: () => ({ from: isoToday(), to: isoDaysAhead(30) }) },
  { label: 'Next 90 days', getDates: () => ({ from: isoToday(), to: isoDaysAhead(90) }) },
  {
    label: 'Up to year end',
    getDates: () => ({ from: isoToday(), to: isoEndOfYear() }),
  },
]

function matchPresetLabel(
  from: string,
  to: string,
  presets: readonly DateRangePreset[],
): string | null {
  for (const p of presets) {
    const d = p.getDates()
    if (d.from === from && d.to === to) return p.label
  }
  return null
}

function buttonLabel(
  from: string,
  to: string,
  presets: readonly DateRangePreset[],
): string {
  return matchPresetLabel(from, to, presets) ?? `${formatDateDMY(from)} – ${formatDateDMY(to)}`
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface DateRangeFilterProps {
  from: string
  to: string
  onChange: (from: string, to: string) => void
  presets?: readonly DateRangePreset[]
}

export function DateRangeFilter({
  from,
  to,
  onChange,
  presets = DEFAULT_PRESETS,
}: DateRangeFilterProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [showCustom, setShowCustom] = useState(false)
  const [customFrom, setCustomFrom] = useState(from)
  const [customTo, setCustomTo] = useState(to)
  const [align, setAlign] = useState<'left' | 'right'>('left')
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
        setShowCustom(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  // Flip horizontal anchor when the dropdown would overflow the viewport's
  // right edge. The default-collapsed menu is ~284 px wide; if the custom
  // calendar is open it can grow to ~520 px (CalendarRangePicker maxWidth).
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return
    const trigger = wrapperRef.current.getBoundingClientRect()
    const dropdownWidth = showCustom ? 520 : 284
    const viewportPad = 16
    const wouldOverflowRight =
      trigger.left + dropdownWidth > window.innerWidth - viewportPad
    setAlign(wouldOverflowRight ? 'right' : 'left')
  }, [open, showCustom])

  function selectPreset(preset: DateRangePreset) {
    const d = preset.getDates()
    onChange(d.from, d.to)
    setOpen(false)
    setShowCustom(false)
  }

  function handleCalendarChange(f: string, t: string) {
    setCustomFrom(f)
    setCustomTo(t)
    if (f && t) {
      onChange(f, t)
      setOpen(false)
      setShowCustom(false)
    }
  }

  const label = from && to ? buttonLabel(from, to, presets) : 'Select range'
  const isCustomActive = open && showCustom

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Date range"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 12px',
          height: 36,
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: open ? 'var(--surface-2)' : 'var(--surface)',
          color: 'var(--text)',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'border-color 150ms ease-out, background 150ms ease-out',
          fontFamily: 'inherit',
        }}
      >
        <Icon name="calendar_today" size={14} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
        {label}
        <Icon
          name="expand_more"
          size={14}
          style={{
            color: 'var(--text-3)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 150ms ease-out',
          }}
        />
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: align === 'left' ? 0 : 'auto',
            right: align === 'right' ? 0 : 'auto',
            zIndex: 200,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 4px 20px rgba(80,56,28,0.13)',
            minWidth: 284,
            padding: '4px 0',
            animation: 'lms-fade-up var(--motion-duration-base) var(--motion-ease-out) both',
          }}
        >
          {presets.map((p) => {
            const d = p.getDates()
            const active = d.from === from && d.to === to
            return (
              <button
                key={p.label}
                type="button"
                role="menuitem"
                onClick={() => selectPreset(p)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 14px',
                  textAlign: 'left',
                  background: active ? 'var(--primary-bg)' : 'transparent',
                  color: active ? 'var(--primary-fg)' : 'var(--text)',
                  border: 'none',
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {p.label}
                {active ? <Icon name="check" size={14} style={{ color: 'var(--steel-blue)' }} /> : null}
              </button>
            )
          })}

          <div style={{ height: 1, margin: '4px 0', background: 'var(--french-gray-2)' }} />

          <button
            type="button"
            role="menuitem"
            onClick={() => setShowCustom((s) => !s)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: '8px 14px',
              textAlign: 'left',
              background: isCustomActive ? 'var(--surface-2)' : 'transparent',
              color: 'var(--text)',
              border: 'none',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Custom range…
            <Icon name={showCustom ? 'expand_less' : 'chevron_right'} size={14} style={{ color: 'var(--text-3)' }} />
          </button>

          {showCustom ? (
            <div style={{ padding: '8px 16px 14px' }}>
              <CalendarRangePicker
                from={customFrom}
                to={customTo}
                onChange={handleCalendarChange}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

